import { createAuthenticatedClient } from "../authenticatedClient";
import { API_BASE_URL } from "../config";
import { safeValidate } from "../safeValidate";
import {
  PermissionsAPIResponse,
  PermissionsAPIResponseSchema,
  PoliciesFilterOptions,
  policiesFilterOptionsSchema,
  PoliciesResponse,
  policiesResponseSchema,
  Policy,
  PolicyPayload,
  policySchema,
  PolicyTagResponse,
  policyTagResponseSchema,
  ResourcesResponse,
  resourcesResponseSchema,
  SuggestionsResponse,
  suggestionsResponseSchema,
} from "../schemas/policies.schema";

const policiesClient = createAuthenticatedClient(`${API_BASE_URL}/ispm/v1`);

interface GetPolicyListParams {
  searchValue?: string;
  sort?: string;
  order?: "asc" | "desc";
  limit?: number;
  offset?: number;
  [key: string]: unknown;
}

export interface QueryPayload {
  query: string;
  limit: number;
  offset: number;
}

export const policiesApi = {
  getUserPermissions: async (): Promise<PermissionsAPIResponse> => {
    const url = "/user/permissions?include_license_display_info=true";
    const data = await policiesClient.get<PermissionsAPIResponse>(url);
    const validated: PermissionsAPIResponse = safeValidate(
      PermissionsAPIResponseSchema,
      data,
      "getUserPermissions",
    ) as PermissionsAPIResponse;
    // Type assertion needed because TypeScript doesn't always infer z.output correctly with nested transformations
    // The zod transform converts regions from string | string[] to string, but TypeScript can't always infer this
    return validated;
  },
  getPoliciesList: async (
    params?: GetPolicyListParams,
  ): Promise<PoliciesResponse> => {
    const { searchValue, sort, order, limit, offset, ...filters } =
      params ?? {};

    const payload = {
      q: searchValue,
      sort,
      order,
      limit: limit || 10,
      offset: offset || 0,
      ...filters,
    };

    const data = await policiesClient.post<unknown>("/policy/list", payload);
    const validated: PoliciesResponse = safeValidate(
      policiesResponseSchema,
      data,
      "getPolicyList",
    ) as PoliciesResponse;
    return validated;
  },
  getPolicyFilters: async (): Promise<PoliciesFilterOptions> => {
    const data = await policiesClient.get<unknown>("/policy/filters");
    const validated: PoliciesFilterOptions = safeValidate(
      policiesFilterOptionsSchema,
      data,
      "getPolicyFilters",
      true, // preserveExtra to keep any additional fields that might be returned by the API but not defined in the schema
    ) as PoliciesFilterOptions;
    // Type assertion needed because TypeScript doesn't always infer z.output correctly with nested transformations
    // The zod transform converts regions from string | string[] to string, but TypeScript can't always infer this
    return validated;
  },

  getPolicyById: async (policyId: string): Promise<Policy> => {
    const data = await policiesClient.get<unknown>(`/policy/${policyId}`);
    const validated: Policy = safeValidate(
      policySchema,
      data,
      "getPolicyById",
    ) as Policy;
    // Type assertion needed because TypeScript doesn't always infer z.output correctly with nested transformations
    // The zod transform converts regions from string | string[] to string, but TypeScript can't always infer this
    return validated;
  },
  getPolicyTags: async (): Promise<PolicyTagResponse> => {
    const data = await policiesClient.get<unknown>("/policy/tags");
    const validated: PolicyTagResponse = safeValidate(
      policyTagResponseSchema,
      data,
      "getPolicyTags",
    ) as PolicyTagResponse;
    // Type assertion needed because TypeScript doesn't always infer z.output correctly with nested transformations
    // The zod transform converts regions from string | string[] to string, but TypeScript can't always infer this
    return validated;
  },
  deletePolicy: async (policyId: string): Promise<void> =>
    await policiesClient.delete(`/policy/${policyId}`),
  createPolicy: async ({
    payload,
  }: {
    payload: PolicyPayload;
  }): Promise<Policy> => await policiesClient.post<Policy>("/policy", payload),
  updatePolicy: async ({
    policyId,
    payload,
  }: {
    policyId: string;
    payload: Partial<PolicyPayload>;
  }): Promise<{ id: string }> => {
    const data = await policiesClient.put<{ id: string }>(
      `/policy/${policyId}`,
      payload,
    );
    return data;
  },
  clonePolicy: async ({
    policyId,
    payload,
  }: {
    policyId: string;
    payload: PolicyPayload;
  }): Promise<{ id: string }> => {
    const data = await policiesClient.post<{ id: string }>(
      `/policy/${policyId}/clone`,
      payload,
    );
    return data;
  },
  queryPolicies: async (payload: QueryPayload): Promise<ResourcesResponse> => {
    const data = await policiesClient.post<unknown>("/policy/execute", payload);
    const validated = safeValidate(
      resourcesResponseSchema,
      data,
      "queryPolicies",
    ) as ResourcesResponse;
    // Type assertion needed because TypeScript doesn't always infer z.output correctly with nested transformations
    // The zod transform converts regions from string | string[] to string, but TypeScript can't always infer this
    return validated;
  },
  querySuggestions: async (payload: {
    text: string;
    cursor_pos: number;
  }): Promise<SuggestionsResponse> => {
    const data = await policiesClient.post<unknown>("/policy/suggest", payload);
    const validated = safeValidate(
      suggestionsResponseSchema,
      data,
      "querySuggestions",
    ) as SuggestionsResponse;
    // Type assertion needed because TypeScript doesn't always infer z.output correctly with nested transformations
    // The zod transform converts regions from string | string[] to string, but TypeScript can't always infer this
    return validated;
  },
};
